import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { venueApi } from '../api/venueApi';
import type { CreateVenueFromPlaceDTO, VenueListResponse } from '../types';

export const venueQueryKeys = {
  all: ['venues'] as const,
  lists: () => [...venueQueryKeys.all, 'list'] as const,
  list: (filters?: unknown) => [...venueQueryKeys.lists(), filters] as const,
  searches: () => [...venueQueryKeys.all, 'search'] as const,
  search: (query: string) => [...venueQueryKeys.searches(), query] as const,
  placeDetails: (placeId: string) => [...venueQueryKeys.all, 'place-details', placeId] as const,
};

export const useVenues = (query?: string) => {
  return useQuery({
    queryKey: venueQueryKeys.list(query),
    queryFn: () => venueApi.list(query),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useSearchPlaces = (query: string, enabled = true) => {
  return useQuery({
    queryKey: venueQueryKeys.search(query),
    queryFn: () => venueApi.searchPlaces(query),
    enabled: enabled && query.length > 0,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

export const usePlaceDetails = (placeId: string, enabled = false) => {
  return useQuery({
    queryKey: venueQueryKeys.placeDetails(placeId),
    queryFn: () => venueApi.getPlaceDetails(placeId),
    enabled: enabled && !!placeId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreateVenueFromPlace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateVenueFromPlaceDTO) => venueApi.createFromPlace(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: venueQueryKeys.all });
      toast.success('Venue created successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create venue');
    },
  });
};

export const useDeleteVenue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (venueId: number) => venueApi.delete(venueId),
    onMutate: async (venueId) => {
      // Cancel any outgoing refetches for all list queries
      await queryClient.cancelQueries({ queryKey: venueQueryKeys.lists() });

      // Get all queries that match the list pattern
      const queries = queryClient.getQueriesData<VenueListResponse>({
        queryKey: venueQueryKeys.lists(),
      });

      // Store all previous data
      const previousQueries = queries.map(([queryKey, data]) => ({
        queryKey,
        data,
      }));

      // Optimistically update all matching queries
      queries.forEach(([queryKey]) => {
        queryClient.setQueryData<VenueListResponse>(queryKey, (old) => {
          if (!old) return old;
          return {
            ...old,
            venues: old.venues.filter((venue) => venue.id !== venueId),
            total_count: old.total_count - 1,
          };
        });
      });

      // Return a context with all previous data
      return { previousQueries };
    },
    onError: (_, __, context) => {
      // If the mutation fails, restore all previous queries
      if (context?.previousQueries) {
        context.previousQueries.forEach(({ queryKey, data }) => {
          if (data) {
            queryClient.setQueryData(queryKey, data);
          }
        });
      }
      toast.error('Failed to delete venue');
    },
    onSuccess: () => {
      toast.success('Venue deleted successfully');
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: venueQueryKeys.all });
    },
  });
};